// src/components/news/NewsContentSection.jsx
import React, { useMemo } from "react";
import RichTextRenderer from "../common/RichTextRenderer";
import { DocumentIcon } from "@heroicons/react/24/outline";

const NewsContentSection = ({ news, onImageClick }) => {
  const { hasContent, hasYoutube, hasMedia } = useMemo(() => {
    const hasContentLocal =
      !!news?.content && Array.isArray(news.content) && news.content.length > 0;
    const hasYoutubeLocal = !!news?.youtube_id;
    const hasMediaLocal =
      Array.isArray(news?.media) && news.media.length > 0;

    return {
      hasContent: hasContentLocal,
      hasYoutube: hasYoutubeLocal,
      hasMedia: hasMediaLocal,
    };
  }, [news]);

  if (!hasContent && !hasYoutube && !hasMedia) {
    return null;
  }

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-5 sm:p-6 md:p-7">
        <div className="space-y-6">
          {/* YouTube Video - show first if exists */}
          {hasYoutube && (
            <div className="bg-black rounded-2xl overflow-hidden shadow-xl">
              <div className="aspect-video">
                <iframe
                  src={`https://www.youtube.com/embed/${news.youtube_id}`}
                  title={news.title || "News video"}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {/* Media (PDF or Images) */}
          {hasMedia && (
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {news.media.map((mediaItem, index) => {
                const isPDF =
                  mediaItem?.formats === null ||
                  mediaItem?.formats === undefined;
                const isImageLocal =
                  !isPDF && mediaItem?.formats !== null;

                const mediaUrl = mediaItem?.url;
                if (!mediaUrl) return null;

                const altText =
                  mediaItem?.alternativeText ||
                  mediaItem?.alt ||
                  mediaItem?.name ||
                  news.title ||
                  `Media ${index + 1}`;

                if (isPDF) {
                  return (
                    <button
                      key={mediaItem.id || index}
                      type="button"
                      onClick={() =>
                        window.open(
                          mediaUrl,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                      className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] text-left"
                    >
                      <div className="aspect-video bg-gradient-to-br from-red-50 to-red-100 flex flex-col items-center justify-center p-6 w-100">
                        <DocumentIcon className="h-16 w-16 text-red-600 mb-3" />
                        <p
                          className="text-sm font-semibold text-gray-900 text-center line-clamp-2"
                          title={altText}
                        >
                          {altText}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          PDF Document
                        </p>
                      </div>
                    </button>
                  );
                }

                if (isImageLocal) {
                  return (
                    <img
                      key={mediaItem.id || index}
                      src={mediaUrl}
                      alt={altText}
                      className="block mx-auto max-w-full max-h-[80vh] w-auto h-auto object-contain cursor-zoom-in transition-transform duration-200 hover:scale-[1.01]"
                      onClick={() => {
                        onImageClick && onImageClick(mediaItem);
                      }}
                    />
                  );
                }

                return null;
              })}
            </div>
          )}

          {/* Text content (newsContent) - always after media */}
          {hasContent && (
            <div className="prose prose-lg max-w-none text-gray-800">
              <RichTextRenderer
                content={news.content}
                textColorClass="text-black"
                linkColorClass="text-blue-600 hover:text-blue-800 underline text-lg lg:text-xl"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsContentSection;


